import { useState, useEffect } from 'react';
import { useArtists, useTasks } from '../hooks/useFirestore';
import { useAuth } from '../lib/AuthContext';
import { collection, onSnapshot, query, orderBy, doc, setDoc, updateDoc } from 'firebase/firestore';
import { db } from '../lib/firebase';
import { Check, ChevronRight } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

// ── War Games Icon ────────────────────────────────────────────
function WarGamesIcon({ size = 16 }) {
  const s = size;
  return (
    <svg width={s} height={s} viewBox="0 0 24 24" fill="none" style={{ flexShrink: 0 }}>
      <ellipse cx="12" cy="14" rx="4" ry="5" fill="url(#dbFlame)" opacity="0.35" />
      <line x1="7" y1="3" x2="11" y2="13" stroke="#e8621a" strokeWidth="1.5" strokeLinecap="round" />
      <polygon points="6,2 8,2 7,4" fill="#e8621a" opacity="0.9" />
      <line x1="17" y1="3" x2="13" y2="13" stroke="#e8621a" strokeWidth="1.5" strokeLinecap="round" />
      <polygon points="16,2 18,2 17,4" fill="#e8621a" opacity="0.9" />
      <line x1="12" y1="2" x2="12" y2="13" stroke="#f0a030" strokeWidth="2" strokeLinecap="round" />
      <polygon points="11,1 13,1 12,3" fill="#f0a030" />
      <line x1="5.5" y1="12" x2="8.5" y2="12" stroke="#e8621a" strokeWidth="1.2" strokeLinecap="round" />
      <line x1="15.5" y1="12" x2="18.5" y2="12" stroke="#e8621a" strokeWidth="1.2" strokeLinecap="round" />
      <line x1="10.5" y1="13.5" x2="13.5" y2="13.5" stroke="#f0a030" strokeWidth="1.4" strokeLinecap="round" />
      <path d="M12 24 C10 21 8 19 9 16 C10 14 12 15 12 17 C12 15 14 13 15 15 C16 18 14 21 12 24Z" fill="url(#dbFlame)" opacity="0.85" />
      <defs>
        <radialGradient id="dbFlame" cx="50%" cy="80%" r="60%">
          <stop offset="0%" stopColor="#f0a030" />
          <stop offset="100%" stopColor="#e8621a" stopOpacity="0" />
        </radialGradient>
      </defs>
    </svg>
  );
}

// ── Moon ─────────────────────────────────────────────────────
const MOON_PHASES = [
  { name: 'New Moon',        emoji: '🌑', start: 0,     end: 1.85  },
  { name: 'Waxing Crescent', emoji: '🌒', start: 1.85,  end: 7.38  },
  { name: 'First Quarter',   emoji: '🌓', start: 7.38,  end: 11.07 },
  { name: 'Waxing Gibbous',  emoji: '🌔', start: 11.07, end: 14.77 },
  { name: 'Full Moon',       emoji: '🌕', start: 14.77, end: 16.61 },
  { name: 'Waning Gibbous',  emoji: '🌖', start: 16.61, end: 22.15 },
  { name: 'Last Quarter',    emoji: '🌗', start: 22.15, end: 25.84 },
  { name: 'Waning Crescent', emoji: '🌘', start: 25.84, end: 29.5  },
];

function getMoonData() {
  const refNewMoon = new Date('2025-01-29T00:00:00Z');
  const now = new Date();
  const daysSinceRef = (now - refNewMoon) / (1000 * 60 * 60 * 24);
  const cycleLength = 29.53059;
  const dayInCycle = ((daysSinceRef % cycleLength) + cycleLength) % cycleLength;
  const phase = MOON_PHASES.find(p => dayInCycle >= p.start && dayInCycle < p.end) || MOON_PHASES[0];
  const daysLeft = Math.ceil(phase.end - dayInCycle);
  const progress = ((dayInCycle - phase.start) / (phase.end - phase.start)) * 100;
  const phaseIdx = MOON_PHASES.indexOf(phase);
  const nextPhase = MOON_PHASES[(phaseIdx + 1) % MOON_PHASES.length];
  const cycleStart = new Date(now);
  cycleStart.setDate(cycleStart.getDate() - Math.floor(dayInCycle - phase.start));
  const cycleEnd = new Date(cycleStart);
  cycleEnd.setDate(cycleStart.getDate() + Math.ceil(phase.end - phase.start));
  const fmt = (d) => d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  return { phase, daysLeft, progress, nextPhase, cycleStart: fmt(cycleStart), cycleEnd: fmt(cycleEnd) };
}

// ── Release Tasks Hook ────────────────────────────────────────
function useReleaseTasks(artistId) {
  const [songMap, setSongMap] = useState({});
  useEffect(() => {
    const q = query(collection(db, 'tasks'), orderBy('createdAt', 'desc'));
    return onSnapshot(q, snap => {
      const tasks = snap.docs.map(d => ({ id: d.id, ...d.data() }));
      const release = tasks.filter(t => t.songId && t.category === 'release');
      const filtered = artistId ? release.filter(t => t.artistId === artistId) : release;
      const map = {};
      for (const t of filtered) {
        if (!map[t.songId]) map[t.songId] = { songTitle: t.songTitle, songId: t.songId, tasks: [] };
        map[t.songId].tasks.push(t);
      }
      setSongMap(map);
    });
  }, [artistId]);
  return songMap;
}

// ── Event Dates Hook ──────────────────────────────────────────
function useEventDates() {
  const [dates, setDates] = useState({});
  useEffect(() => {
    const ref = doc(db, 'meta', 'event_dates');
    return onSnapshot(ref, s => setDates(s.exists() ? s.data() : {}), err => console.warn(err));
  }, []);
  const save = async (key, value) => {
    try { await setDoc(doc(db, 'meta', 'event_dates'), { [key]: value }, { merge: true }); }
    catch(e) { console.error(e); }
  };
  return { dates, save };
}

function formatEventDisplay(entry) {
  if (!entry || !entry.value) return 'TBD';
  if (entry.mode === 'month') {
    const [year, month] = entry.value.split('-');
    return new Date(year, month - 1).toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
  }
  if (entry.mode === 'date') {
    const target = new Date(entry.value);
    const today = new Date(); today.setHours(0,0,0,0);
    const diff = Math.ceil((target - today) / (1000 * 60 * 60 * 24));
    if (diff < 0) return 'Past';
    if (diff === 0) return 'Today';
    return `${diff}d`;
  }
  return 'TBD';
}

// ── Inline Countdown Pill ─────────────────────────────────────
function CountdownPill({ icon, label, eventKey, dates, onSave }) {
  const [editing, setEditing] = useState(false);
  const entry = dates[eventKey] || {};
  const [mode, setMode] = useState(entry.mode || 'date');
  const [value, setValue] = useState(entry.value || '');
  const display = formatEventDisplay(entry);
  const isDate = entry.mode === 'date';
  const daysNum = isDate && entry.value ? Math.ceil((new Date(entry.value) - new Date().setHours(0,0,0,0)) / 86400000) : null;
  const urgent = daysNum !== null && daysNum <= 14;

  return (
    <div style={{ position:'relative' }}>
      <div onClick={() => { setMode(entry.mode||'date'); setValue(entry.value||''); setEditing(v=>!v); }}
        style={{ display:'flex', alignItems:'center', gap:8, padding:'6px 14px', borderRadius:20, background:'rgba(255,255,255,.06)', border:`1px solid ${urgent ? 'rgba(232,98,26,.5)' : 'rgba(100,180,200,.2)'}`, cursor:'pointer', transition:'all .2s' }}
        onMouseEnter={e => e.currentTarget.style.background='rgba(255,255,255,.1)'}
        onMouseLeave={e => e.currentTarget.style.background='rgba(255,255,255,.06)'}>
        <span style={{ fontSize:14 }}>{icon}</span>
        <span style={{ fontSize:10, color:'rgba(180,210,220,.7)', fontFamily:'var(--mono)', letterSpacing:1, textTransform:'uppercase' }}>{label}</span>
        <span style={{ fontSize:12, fontWeight:700, color: urgent ? '#e8621a' : 'rgba(160,220,230,.9)', fontFamily:'var(--mono)' }}>{display}</span>
      </div>
      {editing && (
        <div style={{ position:'absolute', top:'calc(100% + 8px)', left:0, zIndex:100, background:'rgba(10,18,28,.95)', border:'1px solid rgba(100,180,200,.3)', borderRadius:12, padding:16, minWidth:220, backdropFilter:'blur(20px)' }}
          onClick={e => e.stopPropagation()}>
          <div style={{ display:'flex', gap:6, marginBottom:10 }}>
            {['date','month'].map(m => (
              <button key={m} onClick={() => { setMode(m); setValue(''); }}
                style={{ padding:'3px 10px', borderRadius:20, border:`1px solid ${mode===m?'rgba(100,180,200,.6)':'rgba(100,180,200,.2)'}`, background:mode===m?'rgba(100,180,200,.15)':'transparent', color:mode===m?'rgba(160,220,230,.9)':'rgba(160,210,220,.5)', fontSize:10, cursor:'pointer', fontFamily:'var(--mono)' }}>
                {m==='date'?'Date':'Month'}
              </button>
            ))}
          </div>
          {mode==='date' && <input type="date" value={value} onChange={e=>setValue(e.target.value)} style={{ width:'100%', marginBottom:10, fontSize:12, background:'rgba(255,255,255,.08)', border:'1px solid rgba(100,180,200,.3)', color:'rgba(200,230,240,.9)', borderRadius:6, padding:'6px 8px' }}/>}
          {mode==='month' && <input type="month" value={value} onChange={e=>setValue(e.target.value)} style={{ width:'100%', marginBottom:10, fontSize:12, background:'rgba(255,255,255,.08)', border:'1px solid rgba(100,180,200,.3)', color:'rgba(200,230,240,.9)', borderRadius:6, padding:'6px 8px' }}/>}
          <div style={{ display:'flex', gap:6, justifyContent:'flex-end' }}>
            <button onClick={()=>setEditing(false)} style={{ fontSize:11, padding:'4px 10px', borderRadius:6, background:'transparent', border:'1px solid rgba(255,255,255,.15)', color:'rgba(180,200,210,.6)', cursor:'pointer' }}>Cancel</button>
            <button onClick={()=>{ onSave(eventKey,{mode,value}); setEditing(false); }} disabled={!value} style={{ fontSize:11, padding:'4px 10px', borderRadius:6, background:'rgba(100,180,200,.2)', border:'1px solid rgba(100,180,200,.4)', color:'rgba(160,220,230,.9)', cursor:'pointer' }}>Save</button>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Phase quotes ─────────────────────────────────────────────
const PHASE_QUOTES = {
  'New Moon':        'Something is beginning.',
  'Waxing Crescent': 'The work continues.',
  'First Quarter':   'Hold the line.',
  'Waxing Gibbous':  'You are closer than you think.',
  'Full Moon':       'This is the moment.',
  'Waning Gibbous':  'Let it settle.',
  'Last Quarter':    'Release what no longer serves.',
  'Waning Crescent': 'Rest. The cycle turns.',
};

// ── Main Dashboard ────────────────────────────────────────────
export default function Dashboard({ activeArtistId }) {
  const { artists } = useArtists();
  const { tasks } = useTasks();
  const { profile } = useAuth();
  const navigate = useNavigate();
  const songMap = useReleaseTasks(activeArtistId);
  const moon = getMoonData();
  const [viewingSong, setViewingSong] = useState(null);
  const { dates, save } = useEventDates();
  const today = new Date().toLocaleDateString('en-US', { weekday:'long', month:'long', day:'numeric', year:'numeric' });
  const todayKey = new Date().toISOString().split('T')[0];

  // Focus tasks — P0, not done, max 3
  const focusTasks = tasks.filter(t => {
    if (t.priority !== 'P0' || t.status === 'done') return false;
    if (t.personal && t.createdBy !== profile?.email) return false;
    if (activeArtistId && t.artistId && t.artistId !== activeArtistId) return false;
    return true;
  }).slice(0, 3);

  const remainingFocus = tasks.filter(t => {
    if (t.priority !== 'P0' || t.status === 'done') return false;
    if (t.personal && t.createdBy !== profile?.email) return false;
    if (activeArtistId && t.artistId && t.artistId !== activeArtistId) return false;
    return true;
  }).length - 3;

  // Daily check-off state (stored in Firestore meta/daily_focus)
  const [checkedToday, setCheckedToday] = useState({});
  useEffect(() => {
    const ref = doc(db, 'meta', `daily_focus_${todayKey}`);
    return onSnapshot(ref, s => setCheckedToday(s.exists() ? (s.data().checked||{}) : {}), err => console.warn(err));
  }, [todayKey]);

  const toggleCheck = async (taskId) => {
    const ref = doc(db, 'meta', `daily_focus_${todayKey}`);
    const newChecked = { ...checkedToday, [taskId]: !checkedToday[taskId] };
    setCheckedToday(newChecked);
    try { await setDoc(ref, { checked: newChecked, date: todayKey }, { merge: true }); }
    catch(e) { console.error(e); }
  };

  const songs = Object.values(songMap);
  const artistColor = (id) => artists.find(a => a.id === id)?.color || 'var(--purple)';
  const artistName = (id) => artists.find(a => a.id === id)?.name || '';

  return (
    <div style={{ minHeight:'100vh', position:'relative', zIndex:1 }}>

      {/* ── Council background ── */}
      <div style={{
        position:'fixed', inset:0, zIndex:0,
        backgroundImage:'url(/council-bg.png)',
        backgroundSize:'110%', backgroundPosition:'center 30%',
        backgroundAttachment:'fixed',
      }}/>
      {/* Overlay — lighter at top, darker at bottom for content readability */}
      <div style={{
        position:'fixed', inset:0, zIndex:0,
        background:'linear-gradient(180deg, rgba(4,12,20,.45) 0%, rgba(6,16,28,.52) 35%, rgba(4,10,18,.82) 65%, rgba(3,8,16,.92) 100%)',
      }}/>

      {/* ── Content ── */}
      <div style={{ position:'relative', zIndex:1, padding:'24px 30px' }}>

        {/* ── Header + Moon (no card — lives on the page) ── */}
        <div style={{ marginBottom:40, paddingTop:8 }}>

          {/* Countdown pills top right */}
          <div style={{ display:'flex', justifyContent:'flex-end', gap:10, marginBottom:32 }}>
            <CountdownPill icon="🏜️" label="Retreat" eventKey="mojave_retreat" dates={dates} onSave={save} />
            <CountdownPill icon={<WarGamesIcon size={14}/>} label="War Games" eventKey="war_games" dates={dates} onSave={save} />
          </div>

          {/* Moon — no card, just atmosphere */}
          <div style={{ textAlign:'center', marginBottom:8 }}>
            {/* Next phase — the headline */}
            <div style={{ fontSize:13, color:'rgba(160,210,230,.6)', fontFamily:'var(--mono)', letterSpacing:3, textTransform:'uppercase', marginBottom:10, textShadow:'0 1px 8px rgba(0,0,0,.8)' }}>
              next phase
            </div>
            <div style={{ fontSize:44, lineHeight:1, marginBottom:8, filter:'drop-shadow(0 0 24px rgba(200,230,255,.3))' }}>
              {moon.nextPhase.emoji}
            </div>
            <div style={{ fontSize:20, fontWeight:600, color:'rgba(230,245,255,.95)', marginBottom:4, textShadow:'0 2px 16px rgba(0,0,0,.9)' }}>
              {moon.nextPhase.name} · {moon.daysLeft} day{moon.daysLeft !== 1 ? 's' : ''}
            </div>
            {/* Current phase — secondary */}
            <div style={{ fontSize:10, color:'rgba(140,185,210,.5)', fontFamily:'var(--mono)', letterSpacing:2, textTransform:'uppercase', marginBottom:14, textShadow:'0 1px 8px rgba(0,0,0,.8)' }}>
              now · {moon.phase.emoji} {moon.phase.name}
            </div>
            {/* Phase quote */}
            <div style={{ fontSize:24, fontWeight:300, color:'rgba(230,245,255,.95)', letterSpacing:0.5, marginBottom:10, fontStyle:'italic', textShadow:'0 2px 20px rgba(0,0,0,.9), 0 0 40px rgba(0,0,0,.6)' }}>
              {PHASE_QUOTES[moon.phase.name]}
            </div>
            <div style={{ fontSize:10, color:'rgba(160,200,220,.5)', fontFamily:'var(--mono)', letterSpacing:1, marginBottom:20, textShadow:'0 1px 8px rgba(0,0,0,.8)' }}>
              {today}
            </div>
            {/* Progress bar */}
            <div style={{ maxWidth:320, margin:'0 auto', display:'flex', alignItems:'center', gap:10 }}>
              <div style={{ fontSize:9, color:'rgba(140,185,210,.5)', fontFamily:'var(--mono)', width:40, textAlign:'right', textShadow:'0 1px 6px rgba(0,0,0,.8)' }}>
                {moon.cycleStart}
              </div>
              <div style={{ flex:1, height:2, background:'rgba(255,255,255,.12)', borderRadius:2, overflow:'hidden' }}>
                <div style={{ height:'100%', width:`${moon.progress}%`, background:'linear-gradient(90deg, rgba(100,160,220,.7), rgba(160,110,230,.7))', borderRadius:2, transition:'width .5s' }}/>
              </div>
              <div style={{ fontSize:9, color:'rgba(140,185,210,.5)', fontFamily:'var(--mono)', width:40, textShadow:'0 1px 6px rgba(0,0,0,.8)' }}>
                {moon.cycleEnd}
              </div>
            </div>
          </div>
        </div>

        {/* ── Two column grid ── */}
        <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:16 }}>

          {/* ── Focus ── */}
          <div style={{
            borderRadius:16, overflow:'hidden',
            background:'rgba(15,30,48,.65)',
            border:'1px solid rgba(100,170,200,.12)',
            backdropFilter:'blur(12px)',
          }}>
            <div style={{ padding:'16px 20px', borderBottom:'1px solid rgba(100,170,200,.08)', display:'flex', alignItems:'center', justifyContent:'space-between' }}>
              <div>
                <div style={{ fontSize:14, fontWeight:600, color:'rgba(200,230,240,.9)' }}>Focus</div>
                <div style={{ fontSize:10, color:'rgba(120,170,200,.5)', fontFamily:'var(--mono)', marginTop:2 }}>
                  {focusTasks.length === 0 ? 'All clear' : `${focusTasks.length} thing${focusTasks.length !== 1 ? 's' : ''} today`}
                </div>
              </div>
              <button onClick={() => navigate('/tasks')}
                style={{ background:'none', border:'none', cursor:'pointer', color:'rgba(120,170,200,.4)', display:'flex', alignItems:'center', gap:4, fontSize:11, fontFamily:'var(--mono)' }}>
                All <ChevronRight size={12}/>
              </button>
            </div>
            <div style={{ padding:'12px 14px', display:'flex', flexDirection:'column', gap:8, minHeight:80 }}>
              {focusTasks.length === 0 && (
                <div style={{ padding:'24px 0', textAlign:'center', color:'rgba(100,180,160,.6)', fontSize:12, fontFamily:'var(--mono)' }}>
                  ✦ you're clear
                </div>
              )}
              {focusTasks.map(task => {
                const checked = !!checkedToday[task.id];
                return (
                  <div key={task.id}
                    style={{ display:'flex', alignItems:'center', gap:10, padding:'10px 12px', borderRadius:10, background: checked ? 'rgba(80,160,140,.08)' : 'rgba(255,255,255,.04)', border:`1px solid ${checked ? 'rgba(80,180,160,.2)' : 'rgba(100,170,200,.1)'}`, transition:'all .2s' }}>
                    {/* Checkbox */}
                    <div onClick={() => toggleCheck(task.id)}
                      style={{ width:18, height:18, borderRadius:5, border:`1.5px solid ${checked ? 'rgba(80,180,160,.7)' : 'rgba(120,170,200,.3)'}`, background: checked ? 'rgba(80,180,160,.2)' : 'transparent', display:'flex', alignItems:'center', justifyContent:'center', cursor:'pointer', flexShrink:0, transition:'all .2s' }}>
                      {checked && <Check size={10} color="rgba(100,210,180,.9)" strokeWidth={2.5}/>}
                    </div>
                    <div style={{ flex:1, minWidth:0 }}>
                      <div style={{ fontSize:12, fontWeight:500, color: checked ? 'rgba(130,170,190,.4)' : 'rgba(200,225,240,.85)', textDecoration: checked ? 'line-through' : 'none', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap', transition:'all .2s' }}>
                        {task.title}
                      </div>
                      {task.artistId && (
                        <div style={{ fontSize:9, padding:'1px 6px', borderRadius:10, background:artistColor(task.artistId)+'18', color:artistColor(task.artistId), fontFamily:'var(--mono)', fontWeight:600, display:'inline-block', marginTop:3 }}>
                          {artistName(task.artistId)}
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
              {remainingFocus > 0 && (
                <button onClick={() => navigate('/tasks')}
                  style={{ fontSize:11, color:'rgba(100,160,190,.5)', background:'none', border:'none', cursor:'pointer', textAlign:'left', fontFamily:'var(--mono)', padding:'4px 2px' }}>
                  + {remainingFocus} more in tasks →
                </button>
              )}
            </div>
          </div>

          {/* ── Releases ── */}
          <div style={{
            borderRadius:16, overflow:'hidden',
            background:'rgba(15,30,48,.65)',
            border:'1px solid rgba(100,170,200,.12)',
            backdropFilter:'blur(12px)',
          }}>
            <div style={{ padding:'16px 20px', borderBottom:'1px solid rgba(100,170,200,.08)', display:'flex', alignItems:'center', justifyContent:'space-between' }}>
              <div>
                <div style={{ fontSize:14, fontWeight:600, color:'rgba(200,230,240,.9)' }}>Releases</div>
                <div style={{ fontSize:10, color:'rgba(120,170,200,.5)', fontFamily:'var(--mono)', marginTop:2 }}>
                  {songs.length === 0 ? 'Nothing in Flower yet' : `${songs.length} release ready`}
                </div>
              </div>
            </div>
            <div style={{ padding:'12px 14px', display:'flex', flexDirection:'column', gap:8, minHeight:80 }}>
              {songs.length === 0 && (
                <div style={{ padding:'24px 0', textAlign:'center', color:'rgba(100,140,170,.4)', fontSize:12, fontFamily:'var(--mono)' }}>
                  No songs in Flower stage yet
                </div>
              )}
              {songs.map(song => {
                const done = song.tasks.filter(t => t.status === 'done').length;
                const total = song.tasks.length;
                const pct = total ? Math.round((done / total) * 100) : 0;
                return (
                  <div key={song.songId} onClick={() => setViewingSong(song)}
                    style={{ padding:'12px 14px', borderRadius:10, background:'rgba(255,255,255,.04)', border:'1px solid rgba(100,170,200,.1)', cursor:'pointer', transition:'border-color .2s' }}
                    onMouseEnter={e => e.currentTarget.style.borderColor='rgba(100,200,190,.3)'}
                    onMouseLeave={e => e.currentTarget.style.borderColor='rgba(100,170,200,.1)'}>
                    <div style={{ display:'flex', alignItems:'center', gap:8, marginBottom:8 }}>
                      <div style={{ flex:1, fontSize:12, fontWeight:600, color:'rgba(200,225,240,.85)', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{song.songTitle}</div>
                      <div style={{ fontSize:12, fontWeight:700, color: pct===100 ? 'rgba(80,200,160,.8)' : 'rgba(120,190,210,.7)', fontFamily:'var(--mono)', flexShrink:0 }}>
                        {done}/{total}
                      </div>
                    </div>
                    <div style={{ height:3, background:'rgba(255,255,255,.07)', borderRadius:2, overflow:'hidden' }}>
                      <div style={{ height:'100%', width:`${pct}%`, background: pct===100 ? 'rgba(80,200,160,.6)' : 'linear-gradient(90deg, rgba(80,140,200,.6), rgba(120,100,200,.6))', borderRadius:2, transition:'width .4s' }}/>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>

      {/* ── Song checklist modal ── */}
      {viewingSong && (
        <div style={{ position:'fixed', inset:0, zIndex:999, background:'rgba(0,0,0,.75)', backdropFilter:'blur(10px)', display:'flex', alignItems:'center', justifyContent:'center', padding:20 }}
          onClick={() => setViewingSong(null)}>
          <div style={{ background:'rgba(12,24,38,.95)', border:'1px solid rgba(100,170,200,.2)', borderRadius:18, padding:28, maxWidth:480, width:'100%', maxHeight:'80vh', overflowY:'auto' }}
            onClick={e => e.stopPropagation()}>
            <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:20 }}>
              <div>
                <div style={{ fontSize:10, letterSpacing:2, textTransform:'uppercase', color:'rgba(100,190,210,.6)', fontFamily:'var(--mono)', marginBottom:4 }}>Release Checklist</div>
                <div style={{ fontSize:18, fontWeight:700, color:'rgba(210,235,245,.95)' }}>{viewingSong.songTitle}</div>
              </div>
              <button onClick={() => setViewingSong(null)} style={{ background:'none', border:'none', cursor:'pointer', color:'rgba(120,160,190,.4)', fontSize:18 }}>✕</button>
            </div>
            <div style={{ display:'flex', flexDirection:'column', gap:8 }}>
              {viewingSong.tasks.map(task => (
                <div key={task.id} style={{ display:'flex', alignItems:'center', gap:12, padding:'10px 14px', borderRadius:10, background: task.status==='done' ? 'rgba(60,160,130,.08)' : 'rgba(255,255,255,.04)', border:`1px solid ${task.status==='done' ? 'rgba(60,180,140,.2)' : 'rgba(100,160,190,.1)'}` }}>
                  <div style={{ width:18, height:18, borderRadius:'50%', border:`2px solid ${task.status==='done' ? 'rgba(60,180,140,.7)' : 'rgba(100,160,190,.3)'}`, background: task.status==='done' ? 'rgba(60,180,140,.2)' : 'transparent', display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0 }}>
                    {task.status==='done' && <Check size={10} color="rgba(80,210,170,.9)"/>}
                  </div>
                  <div style={{ flex:1, fontSize:12, color: task.status==='done' ? 'rgba(120,160,180,.4)' : 'rgba(190,220,235,.8)', textDecoration: task.status==='done' ? 'line-through' : 'none' }}>{task.title}</div>
                </div>
              ))}
            </div>
            <div style={{ marginTop:16, textAlign:'right' }}>
              <button onClick={() => { navigate('/tasks'); setViewingSong(null); }} style={{ fontSize:12, color:'rgba(100,180,210,.6)', background:'none', border:'none', cursor:'pointer' }}>Manage in Tasks →</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
